import { isUsersApiEnabled, USERS_API_URL } from 'utils/constants'
import ProfileIcon from '../icons/ProfileIcon'
import { useProfileDetails } from './useProfileDetails'
import { useWallet } from '@solana/wallet-adapter-react'
import { useEffect, useState, useMemo } from 'react'
import usePrevious from 'hooks/usePrevious'

export type FallbackStrategy = 'currentUser' | 'placeholder'

export interface ProfileImageProps {
  className?: string
  imageSize: number | `${number}`
  placeholderSize: number | `${number}`
  imageSource?:
    | {
        type: 'currentUserProfile'
      }
    | {
        type: 'customUrl'
        imageUrl: string | null | undefined
        fallbackStrategy?: FallbackStrategy
      }
    | {
        type: 'profileWalletPk'
        profileWalletPk: string | null | undefined
        fallbackStrategy?: FallbackStrategy
      }
}

const ProfileImage = ({
  className,
  imageSize,
  placeholderSize,
  imageSource = { type: 'currentUserProfile' },
}: ProfileImageProps) => {
  const { profileDetails, isFetchingProfileDetails } = useProfileDetails()

  const [fetchTs, setFetchTs] = useState(Date.now())
  const { publicKey } = useWallet()

  const finalImageUrl = useMemo(() => {
    const usersApiOn = isUsersApiEnabled()
    const imageUrl =
      imageSource.type === 'profileWalletPk'
        ? imageSource.profileWalletPk && usersApiOn
          ? `${USERS_API_URL}/user/${imageSource.profileWalletPk}/image`
          : null
        : imageSource.type === 'customUrl'
          ? imageSource.imageUrl || null
          : null

    return imageSource.type === 'currentUserProfile' ||
      (!imageUrl && imageSource.fallbackStrategy === 'currentUser')
      ? publicKey && profileDetails?.hasImage && usersApiOn
        ? `${USERS_API_URL}/user/${publicKey}/image?ts=${fetchTs}`
        : null
      : imageUrl
  }, [
    imageSource.type,
    // @ts-expect-error - discriminated union type without narrowing, we need to add all dependencies and cannot rely on object equality for `imageSource`
    imageSource.profileWalletPk,
    // @ts-expect-error - discriminated union type without narrowing, we need to add all dependencies and cannot rely on object equality for `imageSource`
    imageSource.imageUrl,
    // @ts-expect-error - discriminated union type without narrowing, we need to add all dependencies and cannot rely on object equality for `imageSource`
    imageSource.fallbackStrategy,
    publicKey,
    profileDetails?.hasImage,
    fetchTs,
  ])

  const wasPreviouslyFetching = usePrevious(isFetchingProfileDetails)
  useEffect(() => {
    if (wasPreviouslyFetching && !isFetchingProfileDetails) {
      setFetchTs(Date.now())
    }
  }, [isFetchingProfileDetails, wasPreviouslyFetching])

  return finalImageUrl ? (
    <div
      className={`overflow-hidden rounded-full bg-cover bg-center ${className}`}
      style={{
        width: `${imageSize}px`,
        height: `${imageSize}px`,
        backgroundImage: `url(${finalImageUrl})`,
      }}
    />
  ) : (
    <div
      className={`flex shrink-0 items-center justify-center rounded-full bg-th-bkg-3 ${className}`}
      style={{ width: `${imageSize}px`, height: `${imageSize}px` }}
    >
      <div
        style={{
          width: `${placeholderSize}px`,
          height: `${placeholderSize}px`,
        }}
      >
        <ProfileIcon className="size-full text-th-fgd-3" />
      </div>
    </div>
  )
}

export default ProfileImage
